import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Typography from '@mui/material/Typography'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { Link, router } from '@inertiajs/react'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { useSortable } from '@/hooks/useSortable'

interface CronJob {
  id: number
  name: string
  cron_expression: string
  timezone: string
  enabled: boolean
  next_run_at: string | null
  last_run_at: string | null
  board_name: string
  board_slug: string
  project_name: string
  assignee_username: string | null
  requestor_username: string
  created_at: string
}

interface ProjectRef {
  id: number
  name: string
}

interface Filters {
  project_id: number | null
}

interface Props {
  cron_jobs: CronJob[]
  projects: ProjectRef[]
  filters: Filters
}

function formatDateTime(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString()
}

const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'board_name', label: 'Board' },
  { value: 'enabled', label: 'Status' },
  { value: 'next_run_at', label: 'Next Run' },
  { value: 'created_at', label: 'Created' },
]

export default function Index({ cron_jobs, projects, filters }: Props) {
  const { sorted, order, orderBy, handleSort } = useSortable(cron_jobs, 'name')
  const [deleteTarget, setDeleteTarget] = useState<CronJob | null>(null)

  function applyFilter(key: string, value: any) {
    const newFilters: Record<string, any> = { ...filters, [key]: value || undefined }
    Object.keys(newFilters).forEach(k => {
      if (newFilters[k] === null || newFilters[k] === undefined || newFilters[k] === '' || newFilters[k] === 0) {
        delete newFilters[k]
      }
    })
    router.get('/manage/cron_jobs', newFilters, { preserveState: true })
  }

  function handleDelete() {
    if (deleteTarget) {
      router.delete(`/manage/cron_jobs/${deleteTarget.id}`)
      setDeleteTarget(null)
    }
  }

  return (
    <SidebarLayout
      section="manage"
      title="Cron Jobs"
      action={
        <Button component={Link} href="/manage/cron_jobs/new" variant="contained">
          New cron job
        </Button>
      }
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Project</InputLabel>
          <Select
            value={filters.project_id || ''}
            label="Project"
            onChange={e => applyFilter('project_id', e.target.value || null)}
          >
            <MenuItem value="">All Projects</MenuItem>
            {projects.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {filters.project_id && (
          <Button
            size="small"
            onClick={() => router.get('/manage/cron_jobs', {}, { preserveState: true })}
          >
            Clear
          </Button>
        )}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Sort by</InputLabel>
          <Select
            value={orderBy}
            label="Sort by"
            onChange={e => handleSort(e.target.value)}
          >
            {SORT_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <IconButton size="small" onClick={() => handleSort(orderBy)}>
          {order === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
        </IconButton>
      </Box>

      {sorted.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>
          No cron jobs found. Create one to schedule recurring tasks.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {sorted.map((cronJob) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={cronJob.id}>
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ flexGrow: 1, mr: 1 }}>
                    {cronJob.name}
                  </Typography>
                  <Chip
                    label={cronJob.enabled ? 'Enabled' : 'Disabled'}
                    size="small"
                    variant="outlined"
                    color={cronJob.enabled ? 'success' : 'default'}
                  />
                </Box>

                <Link href={`/tasks/boards/${cronJob.board_slug}/settings`} style={{ color: 'inherit', textDecoration: 'none' }}>
                  <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
                    {cronJob.board_name}
                  </Typography>
                </Link>

                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                  <Chip
                    label={cronJob.cron_expression}
                    size="small"
                    variant="outlined"
                    sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                  />
                  <Chip label={cronJob.timezone} size="small" variant="outlined" />
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5 }}>
                  Next run: {formatDateTime(cronJob.next_run_at)}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                  <Button component={Link} href={`/manage/cron_jobs/${cronJob.id}/edit`} size="small">
                    Edit
                  </Button>
                  <Button size="small" color="error" onClick={() => setDeleteTarget(cronJob)}>
                    Delete
                  </Button>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete cron job"
        message={`Are you sure you want to delete cron job '${deleteTarget?.name}'? This cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </SidebarLayout>
  )
}
